import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, Copy, CheckCircle2, ArrowLeft } from "lucide-react";
import { TIERS, formatRp } from "@/lib/tiers";
import SaweriaAutoCheckout from "@/components/SaweriaAutoCheckout";

const METHODS = [
  { key: "saweria", label: "Saweria" },
  { key: "qris", label: "QRIS" },
  { key: "dana", label: "DANA" },
  { key: "ovo", label: "OVO" },
  { key: "gopay", label: "GoPay" },
  { key: "bank", label: "Bank Transfer" },
];

export default function CheckoutPage() {
  const { tier } = useParams();
  const { user } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  // User's selected method (may be null until they pick or settings load)
  const [method, setMethodState] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [proofUrl, setProofUrl] = useState("");
  const [form, setForm] = useState({ sender_name: user?.name || "", sender_account: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [useManualSaweria, setUseManualSaweria] = useState(false);

  const tierLabel = TIERS[tier]?.label || "Unknown";
  const amount = tier === "elite_cultist" ? settings.elite_price
    : tier === "hardcore_cultist" ? settings.hardcore_price
    : tier === "god_tier_supporter" ? settings.god_tier_price
    : 0;

  // Derive the effective active method: user's pick if still enabled, else first enabled.
  const effectiveMethod = useMemo(() => {
    const pm = settings.payment_methods || {};
    if (method && pm[method]?.enabled !== false) return method;
    const firstEnabled = METHODS.find((mm) => pm[mm.key]?.enabled !== false);
    return firstEnabled ? firstEnabled.key : "saweria";
  }, [method, settings.payment_methods]);

  const setMethod = (key) => setMethodState(key);

  const m = settings.payment_methods?.[effectiveMethod] || {};
  const methodEnabled = settings.payment_methods?.[effectiveMethod]?.enabled !== false;

  useEffect(() => { if (!["elite_cultist", "hardcore_cultist", "god_tier_supporter"].includes(tier)) nav("/membership"); }, [tier, nav]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofUrl(data.url);
      toast.success("Proof uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!proofUrl) { toast.error("Upload your payment proof first"); return; }
    setSubmitting(true);
    try {
      await api.post("/payments/submit", {
        tier, method: effectiveMethod, amount,
        sender_name: form.sender_name, sender_account: form.sender_account, note: form.note, proof_url: proofUrl,
      });
      toast.success("Tribute submitted. Awaiting verification.");
      nav("/dashboard");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <PageShell>
      <section className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/membership" className="inline-flex items-center text-sm text-zinc-400 hover:text-rose-300 mb-6" data-testid="checkout-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to membership
        </Link>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="font-mystic text-rose-400 text-sm">Tribute</div>
            <h1 className="font-display text-3xl font-black text-white">Become a {tierLabel}</h1>
          </div>
          <div className="font-display text-2xl text-white">{formatRp(amount)}</div>
        </div>

        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          {/* Methods */}
          <div className="lg:col-span-7 glass-strong rounded-xl p-6">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Step 1 — Payment Method</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {METHODS.map((mm) => {
                const enabled = settings.payment_methods?.[mm.key]?.enabled !== false;
                const isActive = effectiveMethod === mm.key && enabled;
                return (
                  <button
                    key={mm.key}
                    type="button"
                    onClick={() => { if (enabled) setMethod(mm.key); }}
                    disabled={!enabled}
                    aria-disabled={!enabled}
                    className={
                      `relative p-3 rounded-lg border text-sm font-semibold transition ${
                        !enabled
                          ? "opacity-50 cursor-not-allowed border-white/10 bg-black/30 text-zinc-500"
                          : isActive
                            ? "border-rose-500 bg-rose-950/30 text-white"
                            : "border-white/10 bg-black/30 text-zinc-300 hover:bg-white/5"
                      }`
                    }
                    data-testid={`method-${mm.key}`}
                  >
                    <span>{mm.label}</span>
                    {!enabled && (
                      <span
                        className="absolute -top-2 -right-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 border border-white/10"
                        data-testid={`method-${mm.key}-badge`}
                      >
                        Coming Soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 p-5 rounded-lg bg-black/40 border border-white/10">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Step 2 — Send Payment</div>
              {!methodEnabled ? (
                <div className="text-sm text-zinc-400">This payment method is currently unavailable. Please pick another option above.</div>
              ) : effectiveMethod === "saweria" && !useManualSaweria ? (
                <div className="mt-1">
                  <SaweriaAutoCheckout tier={tier} amount={amount} onFallbackManual={() => setUseManualSaweria(true)} />
                </div>
              ) : effectiveMethod === "saweria" && useManualSaweria ? (
                <div className="flex flex-col items-start gap-3">
                  <div className="flex items-center justify-between w-full">
                    <span className="text-xs text-amber-300 bg-amber-950/40 px-2 py-0.5 rounded border border-amber-500/30">Mode Manual Aktif</span>
                    <button type="button" onClick={() => setUseManualSaweria(false)} className="text-xs text-rose-400 hover:underline">Kembali ke Otomatis</button>
                  </div>
                  <p className="text-sm text-zinc-300">
                    Buka Saweria, selesaikan donasi sebesar <span className="text-white font-semibold">{formatRp(amount)}</span>, lalu unggah bukti transfer di Step 3.
                  </p>
                  <a
                    href={m.url || "https://saweria.co/kyoukareimei"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-semibold transition"
                    data-testid="open-saweria-btn"
                  >
                    Open Saweria
                  </a>
                  <div className="mt-3 space-y-2 text-sm w-full">
                    <Row label="Amount" value={formatRp(amount)} copyable onCopy={() => copy(String(amount))} copied={copied} />
                    {m.instructions && <p className="text-zinc-400 text-xs mt-2">{m.instructions}</p>}
                  </div>
                </div>
              ) : (
                <>
                  {m.qr_image_url ? (
                    <div className="flex flex-col items-center gap-3">
                      <img src={fileUrl(m.qr_image_url)} alt="QR" className="w-56 h-56 object-contain rounded-lg bg-white p-2" />
                      <div className="text-xs text-zinc-400">Scan to pay</div>
                    </div>
                  ) : null}
                  <div className="mt-4 space-y-2 text-sm">
                    {m.account_name && <Row label="Account Name" value={m.account_name} />}
                    {m.account_number && <Row label="Number" value={m.account_number} copyable onCopy={() => copy(m.account_number)} copied={copied} />}
                    <Row label="Amount" value={formatRp(amount)} copyable onCopy={() => copy(String(amount))} copied={copied} />
                    {m.instructions && <p className="text-zinc-400 text-xs mt-3">{m.instructions}</p>}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Proof Upload (Selalu ada, tapi opsional untuk Saweria) */}
          <div className="lg:col-span-5 glass-strong rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Step 3 — Upload Proof</div>
              {effectiveMethod === "saweria" && (
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-950/60 text-emerald-300 border border-emerald-500/30">
                  Opsional (Auto Aktif)
                </span>
              )}
            </div>
            <label className="block border-2 border-dashed border-white/15 hover:border-rose-500/40 rounded-lg p-6 text-center cursor-pointer transition" data-testid="proof-dropzone">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { setProofFile(e.target.files[0]); handleUpload(e.target.files[0]); }} data-testid="proof-file-input" />
              {proofUrl ? (
                <div>
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto" />
                  <div className="mt-2 text-sm text-white">Proof uploaded</div>
                  <img src={fileUrl(proofUrl)} alt="" className="mt-3 max-h-40 mx-auto rounded" />
                  <div className="text-xs text-zinc-500 mt-2">Click to replace</div>
                </div>
              ) : uploading ? (
                <div className="text-rose-300 text-sm font-mystic">Uploading…</div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 text-zinc-400 mx-auto" />
                  <div className="mt-2 text-sm text-zinc-300">Click to upload screenshot</div>
                  <div className="text-xs text-zinc-500 mt-1">JPG/PNG, max 2MB (auto-compressed)</div>
                </div>
              )}
            </label>

            <div className="mt-5 space-y-3">
              <div>
                <Label className="text-zinc-300 text-xs">Sender Name</Label>
                <Input value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="sender-name-input" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Sender Account / Number (optional)</Label>
                <Input value={form.sender_account} onChange={(e) => setForm({ ...form, sender_account: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="sender-account-input" />
              </div>
              <div>
                <Label className="text-zinc-300 text-xs">Note (optional)</Label>
                <Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1 bg-black/40 border-white/10" data-testid="checkout-note-input" />
              </div>
              <Button onClick={submit} disabled={submitting || !proofUrl} className="w-full bg-rose-600 hover:bg-rose-500 py-6 font-semibold" data-testid="checkout-submit-btn">
                {submitting ? "Submitting…" : effectiveMethod === "saweria" ? "Kirim Bukti Screenshot (Opsional)" : "Submit Tribute"}
              </Button>
              <div className="text-xs text-zinc-500 font-mystic italic">
                {effectiveMethod === "saweria" 
                  ? "Verifikasi otomatis aktif via nominal unik. Upload screenshot di atas opsional jika ingin melampirkan bukti untuk admin." 
                  : "Status will switch to Pending Verification. Approval within ~24h."}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

function Row({ label, value, copyable, onCopy, copied }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-500 text-xs uppercase tracking-wider">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-white font-semibold">{value}</span>
        {copyable && (
          <button onClick={onCopy} className="text-zinc-400 hover:text-rose-300" data-testid={`copy-${label}`}>
            {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}
